import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import apiUrl from "../../Apiurl";

const JOB_POLL_INTERVAL_MS = 3000;
const JOB_TERMINAL_STATUSES = new Set(["SUCCESS", "ERROR", "REJECTED"]);
const ORIGIN_LABELS = {
  R2: "archivio R2",
  VIG: "sito VIG (archivio R2 della sorgente popolato ora)",
};

// Copia le immagini del prodotto di riferimento nel prodotto corrente: a
// differenza dei testi agisce subito su R2, VIG, BOT e Moda, non sulla bozza.
function CopiaImmaginiRiferimento({
  codiceVS,
  sourceCodVS,
  token,
  requestedBy,
  brandCode,
  className,
  onScheduled,
}) {
  const [state, setState] = useState({ pending: false, error: "", result: null });
  const [job, setJob] = useState(null);
  const jobId = state.result?.jobId ?? null;
  const onScheduledRef = useRef(onScheduled);
  onScheduledRef.current = onScheduled;

  useEffect(() => {
    setState({ pending: false, error: "", result: null });
    setJob(null);
  }, [codiceVS, sourceCodVS]);

  useEffect(() => {
    if (!jobId || !token || !codiceVS) return undefined;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/moda/articoli/${encodeURIComponent(codiceVS)}/immagini/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        const current = response?.data?.job ?? null;
        setJob(current);
        if (current && JOB_TERMINAL_STATUSES.has(current.status)) {
          if (typeof onScheduledRef.current === "function") {
            onScheduledRef.current({ action: "images-copied", jobId, status: current.status });
          }
          return;
        }
      } catch (error) {
        if (cancelled) return;
        setJob((previous) => ({
          ...(previous || {}),
          pollError:
            error?.response?.data?.error || "Stato del job immagini non disponibile.",
        }));
      }
      timer = window.setTimeout(poll, JOB_POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [jobId, token, codiceVS]);

  const sameProduct =
    String(codiceVS || "").trim().toUpperCase() ===
    String(sourceCodVS || "").trim().toUpperCase();
  const jobRunning = Boolean(jobId) && !JOB_TERMINAL_STATUSES.has(job?.status);
  const disabledReason = !codiceVS
    ? "Salva prima il prodotto corrente."
    : sameProduct
    ? "Il prodotto di riferimento coincide con quello corrente."
    : !requestedBy
    ? "Username Domino non disponibile: impossibile registrare la copia."
    : !token
    ? "Autenticazione in corso."
    : "";

  const copia = async () => {
    const conferma = window.confirm(
      `Le immagini di ${codiceVS} su R2, VIG, BOT e Moda verranno sostituite con quelle di ${sourceCodVS}. Procedere?`,
    );
    if (!conferma) return;
    setState({ pending: true, error: "", result: null });
    setJob(null);
    try {
      const response = await axios.post(
        `${apiUrl}/moda/articoli/${encodeURIComponent(codiceVS)}/immagini-library/copy-from`,
        { sourceCodVS, requestedBy, brandCode: brandCode || undefined },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const result = response?.data ?? null;
      setState({ pending: false, error: "", result });
      if (typeof onScheduled === "function") {
        onScheduled({ action: "images-copy-scheduled", jobId: result?.jobId });
      }
    } catch (error) {
      setState({
        pending: false,
        error: error?.response?.data?.error || "Copia immagini non avviata.",
        result: null,
      });
    }
  };

  return (
    <div className="mt3">
      <button
        type="button"
        className={className}
        onClick={copia}
        disabled={Boolean(disabledReason) || state.pending || jobRunning}
      >
        {state.pending ? "Copio immagini…" : "Copia immagini"}
      </button>
      <p>
        Prende le immagini di {sourceCodVS} dall&apos;archivio R2 (se mancano le
        recupera prima da VIG) e avvia il job completo per {codiceVS || "questo prodotto"}:
        nuova revisione R2 e invio a VIG, BOT e Moda secondo le destinazioni di
        pubblicazione.
      </p>
      {disabledReason && <p className="f7 gray mt0">{disabledReason}</p>}
      {state.error && <p className="dark-red f6 mt1">{state.error}</p>}
      {state.result && (
        <p className="dark-green f6 mt1 mb1">
          {state.result.imageCount} immagini copiate da {ORIGIN_LABELS[state.result.origin] || state.result.origin}
          {" "}nella revisione R2 {state.result.targetRevision?.version}. Job {state.result.jobId}.
        </p>
      )}
      {job && (
        <p className={`f6 mt0 ${job.status === "SUCCESS" ? "dark-green" : job.status === "ERROR" || job.status === "REJECTED" ? "dark-red" : "mid-gray"}`}>
          {job.status ? `${job.status}: ` : ""}
          {job.lastStep || ""}
          {job.message ? ` – ${job.message}` : ""}
          {job.pollError ? ` (${job.pollError})` : ""}
        </p>
      )}
    </div>
  );
}

export default CopiaImmaginiRiferimento;
